import React, { useState, useMemo } from "react";
import MainLayout from "../components/layout/MainLayout";
import { Card, CardContent } from "../components/ui/card";
import initialSubscriptionsData from "../data/subscriptions.json";
import {
  Calendar,
  DollarSign,
  TrendingUp,
  AlertCircle,
  Clock,
  CheckCircle2,
  ExternalLink,
  Search,
  Filter,
  RefreshCw,
  Globe,
  Server,
  Mail,
  ShieldCheck,
  CreditCard,
  Layers,
  Sparkles,
  ArrowRight,
  TrendingDown,
  Layout,
  Code,
  FileText
} from "lucide-react";

export interface SubscriptionItem {
  id: string;
  provider: string;
  name: string;
  category: "esp" | "domains" | "hosting" | "marketing" | "legal" | "tools" | "finance" | "telecom" | "saas";
  logo: string;
  billingFrequency: "Monthly" | "Annual";
  nextRenewalDate: string;
  daysLeft: number;
  amount: number;
  currency: string;
  amountDKK: number;
  annualCostDKK: number;
  paymentMethod: string;
  source: string;
  autoRenew: boolean;
  status: string;
  notes?: string;
  optimizationTip?: string;
}

export type CategoryFilter = "all" | "esp" | "domains" | "hosting" | "marketing" | "tools" | "finance";
export type HorizonFilter = "all" | "7d" | "30d" | "90d" | "year";

export default function AccountingRenewalsPage() {
  const [data] = useState(initialSubscriptionsData);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>("all");
  const [selectedHorizon, setSelectedHorizon] = useState<HorizonFilter>("all");
  const [sortBy, setSortBy] = useState<"date" | "cost" | "provider">("date");

  const subscriptions: SubscriptionItem[] = useMemo(() => {
    return (data.subscriptions as SubscriptionItem[]) || [];
  }, [data]);

  // Filter subscriptions
  const filteredSubscriptions = useMemo(() => {
    return subscriptions.filter((sub) => {
      // Horizon filter
      if (selectedHorizon === "7d" && (sub.daysLeft < 0 || sub.daysLeft > 7)) return false;
      if (selectedHorizon === "30d" && (sub.daysLeft < 0 || sub.daysLeft > 30)) return false;
      if (selectedHorizon === "90d" && (sub.daysLeft < 0 || sub.daysLeft > 90)) return false;
      if (selectedHorizon === "year" && sub.daysLeft > 365) return false;

      // Category filter
      if (selectedCategory !== "all") {
        if (selectedCategory === "tools" && !["tools", "legal", "saas"].includes(sub.category)) return false;
        else if (selectedCategory !== "tools" && sub.category !== selectedCategory) return false;
      }

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchName = sub.name.toLowerCase().includes(q);
        const matchProvider = sub.provider.toLowerCase().includes(q);
        const matchNotes = (sub.notes || "").toLowerCase().includes(q);
        if (!matchName && !matchProvider && !matchNotes) return false;
      }

      return true;
    }).sort((a, b) => {
      if (sortBy === "date") return a.daysLeft - b.daysLeft;
      if (sortBy === "cost") return b.amountDKK - a.amountDKK;
      return a.provider.localeCompare(b.provider);
    });
  }, [subscriptions, selectedHorizon, selectedCategory, searchQuery, sortBy]);

  // Dynamic calculations
  const within7Days = useMemo(() => subscriptions.filter(s => s.daysLeft >= 0 && s.daysLeft <= 7), [subscriptions]);
  const within30Days = useMemo(() => subscriptions.filter(s => s.daysLeft >= 0 && s.daysLeft <= 30), [subscriptions]);
  const totalMonthlyRunRateDKK = useMemo(() => {
    return subscriptions
      .filter(s => s.billingFrequency === "Monthly")
      .reduce((sum, s) => sum + s.amountDKK, 0);
  }, [subscriptions]);
  const totalAnnualCostDKK = useMemo(() => {
    return subscriptions.reduce((sum, s) => sum + s.annualCostDKK, 0);
  }, [subscriptions]);

  const getCategoryBadge = (cat: string) => {
    switch (cat) {
      case "esp":
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200"><Mail size={12} /> ESP / Email</span>;
      case "domains":
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"><Globe size={12} /> Domains & DNS</span>;
      case "hosting":
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"><Server size={12} /> Cloud & Hosting</span>;
      case "marketing":
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200"><TrendingUp size={12} /> Marketing</span>;
      case "legal":
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-50 text-slate-700 border border-slate-200"><ShieldCheck size={12} /> Compliance</span>;
      case "finance":
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"><CreditCard size={12} /> Operations</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200"><Layers size={12} /> Software / SaaS</span>;
    }
  };

  const getDaysBadge = (days: number) => {
    if (days < 0) {
      return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-700">Due today</span>;
    }
    if (days <= 3) {
      return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 animate-pulse"><AlertCircle size={13} /> in {days} day{days === 1 ? '' : 's'}</span>;
    }
    if (days <= 7) {
      return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200"><Clock size={13} /> in {days} days</span>;
    }
    if (days <= 30) {
      return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"><Clock size={13} /> in {days} days</span>;
    }
    return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">in {days} days</span>;
  };

  return (
    <MainLayout>
      <div className="space-y-6 pb-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full border border-indigo-200">
                Accounting & Cash Flow
              </span>
              <span className="text-xs text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200 flex items-center gap-1">
                <CheckCircle2 size={12} /> Pleo Live Connected
              </span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mt-2">Upcoming Renewals & Subscriptions</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Live recurring cost tracking across Pleo company cards, GoDaddy, Simply.com, Cloudflare, and ESPs.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-200">
              Synced: {data.summary?.lastUpdated || "Live"}
            </span>
          </div>
        </div>

        {/* KPI Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Due in 7 days */}
          <Card className="border-red-200 bg-gradient-to-br from-red-50/50 to-white shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-red-600 uppercase tracking-wider">Due in Next 7 Days</p>
                <div className="p-2 bg-red-100 rounded-lg text-red-600"><AlertCircle size={18} /></div>
              </div>
              <div className="mt-3">
                <h3 className="text-2xl font-bold text-gray-900">
                  {within7Days.reduce((sum, s) => sum + s.amountDKK, 0).toLocaleString("da-DK", { maximumFractionDigits: 0 })} <span className="text-sm font-medium text-gray-500">DKK</span>
                </h3>
                <p className="text-xs text-red-600 mt-1 font-medium">{within7Days.length} urgent renewals pending</p>
              </div>
            </CardContent>
          </Card>

          {/* Due in 30 days */}
          <Card className="border-amber-200 bg-gradient-to-br from-amber-50/50 to-white shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Due in Next 30 Days</p>
                <div className="p-2 bg-amber-100 rounded-lg text-amber-700"><Calendar size={18} /></div>
              </div>
              <div className="mt-3">
                <h3 className="text-2xl font-bold text-gray-900">
                  {within30Days.reduce((sum, s) => sum + s.amountDKK, 0).toLocaleString("da-DK", { maximumFractionDigits: 0 })} <span className="text-sm font-medium text-gray-500">DKK</span>
                </h3>
                <p className="text-xs text-amber-700 mt-1 font-medium">{within30Days.length} subscriptions in next 30 days</p>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Run Rate */}
          <Card className="border-indigo-100 bg-gradient-to-br from-indigo-50/30 to-white shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">Monthly SaaS Run-rate</p>
                <div className="p-2 bg-indigo-100 rounded-lg text-indigo-700"><CreditCard size={18} /></div>
              </div>
              <div className="mt-3">
                <h3 className="text-2xl font-bold text-gray-900">
                  {totalMonthlyRunRateDKK.toLocaleString("da-DK", { maximumFractionDigits: 0 })} <span className="text-sm font-medium text-gray-500">DKK / mo</span>
                </h3>
                <p className="text-xs text-gray-500 mt-1 font-medium">~{(totalAnnualCostDKK / 1000).toFixed(0)}k DKK / year total spend</p>
              </div>
            </CardContent>
          </Card>

          {/* Savings Identified */}
          <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Identified Savings</p>
                <div className="p-2 bg-emerald-100 rounded-lg text-emerald-700"><TrendingDown size={18} /></div>
              </div>
              <div className="mt-3">
                <h3 className="text-2xl font-bold text-emerald-600">
                  ~3,600 <span className="text-sm font-medium text-emerald-700">DKK / yr</span>
                </h3>
                <p className="text-xs text-emerald-700 mt-1 font-medium">Cloudflare Simplyloan Pro downgrade</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Optimization Callout Card: Cloudflare Simplyloan Pro */}
        <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-xl p-5 border border-indigo-100 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-white rounded-lg shadow-sm text-indigo-600 mt-0.5">
                <Sparkles size={20} />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Immediate Cost Reduction Opportunity: Cloudflare Pro Websites</h4>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>simplyloan.no</strong> and <strong>simplyloan.se</strong> are on paid Cloudflare <strong>Pro Website ($20/mo each)</strong> plans. Since migrating to Lightsail with free Let's Encrypt SSL, you can safely downgrade both to the <strong>Free Website</strong> tier, saving <strong>~$480/yr (~3,600 DKK/yr)</strong> without any performance loss.
                </p>
              </div>
            </div>
            <a
              href="https://dash.cloudflare.com/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold whitespace-nowrap shadow-sm transition-colors"
            >
              Open Cloudflare Dash <ExternalLink size={13} />
            </a>
          </div>
        </div>

        {/* Filters & Search Toolbar */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by service, provider, domain, or notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all"
              />
            </div>

            {/* Time Horizon Filter */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0">
              <span className="text-xs font-medium text-gray-400 mr-1 hidden sm:inline">Horizon:</span>
              {[
                { id: "all", label: "All Year" },
                { id: "7d", label: "Next 7 Days" },
                { id: "30d", label: "Next 30 Days" },
                { id: "90d", label: "Next 90 Days" },
              ].map((h) => (
                <button
                  key={h.id}
                  onClick={() => setSelectedHorizon(h.id as HorizonFilter)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    selectedHorizon === h.id
                      ? "bg-indigo-600 text-white shadow-sm"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {h.label}
                </button>
              ))}
            </div>

            {/* Sorting */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-400 hidden sm:inline">Sort:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="date">Soonest Renewal</option>
                <option value="cost">Highest Cost (DKK)</option>
                <option value="provider">Provider (A-Z)</option>
              </select>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 border-t border-gray-100 pt-3 overflow-x-auto pb-1">
            {[
              { id: "all", label: `All (${subscriptions.length})` },
              { id: "esp", label: `Email & ESPs (${subscriptions.filter(s => s.category === 'esp').length})` },
              { id: "hosting", label: `Cloud & Hosting (${subscriptions.filter(s => s.category === 'hosting').length})` },
              { id: "domains", label: `Domains & DNS (${subscriptions.filter(s => s.category === 'domains').length})` },
              { id: "marketing", label: `Marketing & Tracking (${subscriptions.filter(s => s.category === 'marketing').length})` },
              { id: "tools", label: `Tools & SaaS (${subscriptions.filter(s => ['tools', 'legal', 'saas'].includes(s.category)).length})` },
              { id: "finance", label: `Operations (${subscriptions.filter(s => s.category === 'finance' || s.category === 'telecom').length})` },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedCategory(tab.id as CategoryFilter)}
                className={`px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === tab.id
                    ? "bg-indigo-50 text-indigo-700 border border-indigo-200 font-semibold"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Subscriptions Table / List */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-600">
              <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase font-semibold text-gray-500 tracking-wider">
                <tr>
                  <th className="px-5 py-3.5">Service & Details</th>
                  <th className="px-4 py-3.5">Category</th>
                  <th className="px-4 py-3.5">Cycle</th>
                  <th className="px-4 py-3.5">Renewal Date</th>
                  <th className="px-4 py-3.5">Countdown</th>
                  <th className="px-5 py-3.5 text-right">Cost (DKK)</th>
                  <th className="px-4 py-3.5">Payment Method</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 font-normal">
                {filteredSubscriptions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center text-gray-500">
                      <Layers size={32} className="mx-auto text-gray-300 mb-2" />
                      <p className="font-medium text-gray-700">No subscriptions matching filters</p>
                      <p className="text-xs text-gray-400 mt-0.5">Try clearing your search query or adjusting the time horizon.</p>
                    </td>
                  </tr>
                ) : (
                  filteredSubscriptions.map((sub) => (
                    <tr key={sub.id} className="hover:bg-gray-50/80 transition-colors">
                      <td className="px-5 py-4">
                        <div className="font-semibold text-gray-900 flex items-center gap-2">
                          {sub.name}
                          {sub.optimizationTip && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                              <TrendingDown size={10} /> Tip
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                          <span className="font-medium text-gray-700">{sub.provider}</span>
                          {sub.notes && (
                            <>
                              <span>•</span>
                              <span className="truncate max-w-xs" title={sub.notes}>{sub.notes}</span>
                            </>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap">
                        {getCategoryBadge(sub.category)}
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap text-xs">
                        <span className={`px-2 py-0.5 rounded font-medium ${
                          sub.billingFrequency === "Annual"
                            ? "bg-purple-50 text-purple-700 border border-purple-200"
                            : "bg-blue-50 text-blue-700 border border-blue-200"
                        }`}>
                          {sub.billingFrequency}
                        </span>
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap text-xs font-mono font-medium text-gray-800">
                        {sub.nextRenewalDate}
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap">
                        {getDaysBadge(sub.daysLeft)}
                      </td>

                      <td className="px-5 py-4 whitespace-nowrap text-right">
                        <div className="font-bold text-gray-900 font-mono">
                          {sub.amountDKK.toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DKK
                        </div>
                        {sub.currency !== "DKK" && (
                          <div className="text-[11px] text-gray-400 font-mono">
                            ({sub.amount} {sub.currency})
                          </div>
                        )}
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap text-xs text-gray-600">
                        <div className="flex items-center gap-1.5 font-medium">
                          <CreditCard size={13} className="text-gray-400" />
                          <span>{sub.paymentMethod}</span>
                        </div>
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          via {sub.source}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer Summary Info */}
        <div className="text-xs text-gray-400 flex flex-col sm:flex-row items-center justify-between gap-2 px-2">
          <span>Showing {filteredSubscriptions.length} of {subscriptions.length} active business subscriptions</span>
          <span>Data synchronized via Pleo OpenAPI, GoDaddy API, Simply.com API, and Cloudflare</span>
        </div>
      </div>
    </MainLayout>
  );
}
